import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import MarkdownPreview from "@uiw/react-markdown-preview";
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Pin,
  PinOff,
  Search,
  Copy,
  ArrowLeft,
  Loader2,
} from "lucide-react";

function slugify(text) {
  return (text || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

const BLANK_GUIDE = {
  slug: "",
  title: "",
  summary: "",
  category: "General",
  content: "",
  order: 0,
  is_pinned: false,
  is_published: true,
  cover_url: "",
};

export function GuidesPanel({ axios }) {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // guide being viewed
  const [editing, setEditing] = useState(null); // guide being edited (or BLANK_GUIDE for new)

  const load = () => {
    setLoading(true);
    axios
      .get("/admin/guides")
      .then((r) => setGuides(r.data || []))
      .catch(() => toast.error("Failed to load guides."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [axios]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guides;
    return guides.filter(
      (g) =>
        g.title.toLowerCase().includes(q) ||
        (g.summary || "").toLowerCase().includes(q) ||
        (g.category || "").toLowerCase().includes(q) ||
        (g.content || "").toLowerCase().includes(q)
    );
  }, [guides, query]);

  const grouped = useMemo(() => {
    const m = {};
    filtered.forEach((g) => {
      const cat = g.category || "General";
      (m[cat] = m[cat] || []).push(g);
    });
    return m;
  }, [filtered]);

  const remove = async (slug) => {
    if (!window.confirm("Delete this guide? This can't be undone.")) return;
    try {
      await axios.delete(`/admin/guides/${slug}`);
      setGuides((prev) => prev.filter((g) => g.slug !== slug));
      toast.success("Guide deleted.");
      if (selected?.slug === slug) setSelected(null);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const togglePin = async (g) => {
    try {
      const r = await axios.put(`/admin/guides/${g.slug}`, {
        ...g,
        is_pinned: !g.is_pinned,
      });
      setGuides((prev) => prev.map((x) => (x.slug === g.slug ? r.data : x)));
      toast.success(!g.is_pinned ? "Pinned to top." : "Unpinned.");
    } catch {
      toast.error("Failed to toggle pin.");
    }
  };

  // Reader view
  if (selected) {
    return (
      <GuideReader
        guide={selected}
        onBack={() => setSelected(null)}
        onEdit={() => {
          setEditing(selected);
          setSelected(null);
        }}
        onDelete={() => remove(selected.slug)}
      />
    );
  }

  // Editor view
  if (editing) {
    return (
      <GuideEditor
        axios={axios}
        initial={editing}
        isNew={!editing.slug || !guides.some((g) => g.slug === editing.slug)}
        onCancel={() => setEditing(null)}
        onSaved={(saved) => {
          setEditing(null);
          load();
          setSelected(saved);
        }}
      />
    );
  }

  return (
    <div data-testid="guides-panel" className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">Team guides</h2>
          <p className="text-[#6B4954] text-sm mt-2 leading-relaxed max-w-2xl">
            Internal knowledge base — setup guides, playbooks, and integration
            walkthroughs for the team. All admins can view and edit. Role-based
            access will be added later.
          </p>
        </div>
        <button
          data-testid="guides-new"
          onClick={() => setEditing({ ...BLANK_GUIDE })}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
        >
          <Plus size={14} /> New guide
        </button>
      </header>

      <div className="flex items-center gap-2 border-b border-[#451C25]/15 focus-within:border-[#451C25]">
        <Search size={14} className="text-[#6B4954]" />
        <input
          data-testid="guides-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title, category or content..."
          className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="text-[#6B4954] text-sm py-12">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#451C25]/20 p-10 text-center text-[#6B4954] text-sm">
          {query
            ? "No guides match this search."
            : 'No guides yet. Click "New guide" to create the first one.'}
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([category, items]) => (
            <section key={category} data-testid={`guides-cat-${category}`}>
              <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#451C25] mb-4">
                {category} · {items.length}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((g) => (
                  <GuideCard
                    key={g.slug}
                    guide={g}
                    onOpen={() => setSelected(g)}
                    onEdit={() => setEditing(g)}
                    onDelete={() => remove(g.slug)}
                    onTogglePin={() => togglePin(g)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function GuideCard({ guide, onOpen, onEdit, onDelete, onTogglePin }) {
  return (
    <article
      data-testid={`guide-card-${guide.slug}`}
      className="bg-white border border-[#451C25]/10 p-5 flex flex-col gap-3 hover:border-[#451C25]/60 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={onOpen}
          className="text-left flex-1"
          data-testid={`guide-open-${guide.slug}`}
        >
          <div className="flex items-center gap-2 mb-1">
            {guide.is_pinned && (
              <Pin size={12} className="text-[#451C25] shrink-0" />
            )}
            {!guide.is_published && (
              <span className="text-[9px] tracking-[0.22em] uppercase bg-[#6B4954]/10 text-[#6B4954] px-1.5 py-0.5">
                Draft
              </span>
            )}
          </div>
          <h4 className="font-serif text-xl tracking-tight text-[#451C25] leading-snug">
            {guide.title}
          </h4>
          {guide.summary && (
            <p className="text-[#6B4954] text-[13px] leading-relaxed mt-2 line-clamp-2">
              {guide.summary}
            </p>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onTogglePin}
            title={guide.is_pinned ? "Unpin" : "Pin to top"}
            className="p-1.5 text-[#6B4954] hover:text-[#451C25]"
            data-testid={`guide-pin-${guide.slug}`}
          >
            {guide.is_pinned ? <PinOff size={13} /> : <Pin size={13} />}
          </button>
          <button
            onClick={onEdit}
            title="Edit"
            className="p-1.5 text-[#6B4954] hover:text-[#451C25]"
            data-testid={`guide-edit-${guide.slug}`}
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={onDelete}
            title="Delete"
            className="p-1.5 text-[#6B4954] hover:text-[#2B1017]"
            data-testid={`guide-delete-${guide.slug}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954]/70">
        /{guide.slug} · Updated{" "}
        {new Date(guide.updated_at).toLocaleDateString()}
      </div>
    </article>
  );
}

function GuideReader({ guide, onBack, onEdit, onDelete }) {
  const [copiedBlock, setCopiedBlock] = useState(null);
  const copyContent = async () => {
    try {
      await navigator.clipboard.writeText(guide.content);
      setCopiedBlock("all");
      toast.success("Guide copied to clipboard.");
      setTimeout(() => setCopiedBlock(null), 2000);
    } catch {
      toast.error("Copy failed.");
    }
  };
  return (
    <article data-testid="guide-reader" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            data-testid="guide-back"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25] mb-4"
          >
            <ArrowLeft size={12} /> All guides
          </button>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#451C25]">
              {guide.category}
            </span>
            {guide.is_pinned && <Pin size={12} className="text-[#451C25]" />}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            {guide.title}
          </h1>
          {guide.summary && (
            <p className="text-[#6B4954] text-base leading-relaxed mt-4 max-w-3xl">
              {guide.summary}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="guide-copy-all"
            onClick={copyContent}
            className="inline-flex items-center gap-2 border border-[#451C25]/25 text-[#451C25] px-4 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-[#451C25] hover:text-white transition-colors"
          >
            <Copy size={12} />
            {copiedBlock === "all" ? "Copied!" : "Copy all"}
          </button>
          <button
            data-testid="guide-edit-cta"
            onClick={onEdit}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-4 py-2 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017]"
          >
            <Pencil size={12} /> Edit
          </button>
          <button
            data-testid="guide-delete-cta"
            onClick={onDelete}
            title="Delete"
            className="p-2 text-[#6B4954] hover:text-[#2B1017] border border-[#451C25]/25"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </header>

      <div className="bg-white border border-[#451C25]/10 p-6 md:p-10" data-color-mode="light">
        <MarkdownPreview
          source={guide.content || ""}
          style={{ background: "transparent", fontSize: 15, lineHeight: 1.7 }}
        />
      </div>
    </article>
  );
}

function GuideEditor({ axios, initial, isNew, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...BLANK_GUIDE, ...initial });
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(!initial.slug);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (autoSlug && form.title) {
      setForm((f) => ({ ...f, slug: slugify(form.title) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, autoSlug]);

  const save = async () => {
    if (!form.title.trim() || !form.slug.trim() || !form.content.trim()) {
      toast.error("Title, slug and content are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        summary: form.summary.trim(),
        category: form.category.trim() || "General",
        content: form.content,
        order: Number(form.order) || 0,
        is_pinned: !!form.is_pinned,
        is_published: !!form.is_published,
        cover_url: form.cover_url.trim() || null,
      };
      const res = isNew
        ? await axios.post("/admin/guides", payload)
        : await axios.put(`/admin/guides/${initial.slug}`, payload);
      toast.success(isNew ? "Guide created." : "Guide saved.");
      onSaved(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="guide-editor" className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            data-testid="guide-editor-back"
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25] mb-2"
          >
            <ArrowLeft size={12} /> Cancel
          </button>
          <h2 className="font-serif text-3xl tracking-tight">
            {isNew ? "New guide" : "Edit guide"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="guide-editor-cancel"
            onClick={onCancel}
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25]"
          >
            <X size={12} /> Cancel
          </button>
          <button
            data-testid="guide-editor-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] disabled:opacity-50"
          >
            {saving ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Save size={12} />
            )}
            Save guide
          </button>
        </div>
      </header>

      <div className="bg-white border border-[#451C25]/10 p-6 space-y-5">
        <Field
          label="Title"
          value={form.title}
          onChange={(v) => set("title", v)}
          placeholder="How to connect Zapier"
          testid="guide-field-title"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field
            label="Slug (URL id)"
            value={form.slug}
            onChange={(v) => {
              setAutoSlug(false);
              set("slug", slugify(v));
            }}
            placeholder="how-to-connect-zapier"
            testid="guide-field-slug"
          />
          <Field
            label="Category"
            value={form.category}
            onChange={(v) => set("category", v)}
            placeholder="Integrations"
            testid="guide-field-category"
            list="guide-category-suggestions"
          />
          <Field
            label="Order (sort)"
            value={String(form.order)}
            onChange={(v) => set("order", v.replace(/[^0-9-]/g, ""))}
            placeholder="0"
            testid="guide-field-order"
          />
          <datalist id="guide-category-suggestions">
            {["Integrations", "Sales", "Marketing", "Operations", "Onboarding", "General"].map(
              (c) => (
                <option key={c} value={c} />
              )
            )}
          </datalist>
        </div>

        <Field
          label="Summary (shown on the card)"
          multiline
          value={form.summary}
          onChange={(v) => set("summary", v)}
          placeholder="1-2 sentences describing what this guide is about."
          testid="guide-field-summary"
        />

        <div className="flex flex-wrap items-center gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm text-[#451C25]">
            <input
              data-testid="guide-field-pinned"
              type="checkbox"
              checked={form.is_pinned}
              onChange={(e) => set("is_pinned", e.target.checked)}
              className="accent-[#451C25]"
            />
            Pin to top
          </label>
          <label className="flex items-center gap-2 text-sm text-[#451C25]">
            <input
              data-testid="guide-field-published"
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => set("is_published", e.target.checked)}
              className="accent-[#451C25]"
            />
            Published
          </label>
        </div>

        <div>
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Content (Markdown)
          </label>
          <div data-color-mode="light">
            <MDEditor
              value={form.content}
              onChange={(v) => set("content", v || "")}
              height={520}
              data-testid="guide-field-content"
            />
          </div>
          <p className="mt-2 text-[10px] text-[#6B4954]">
            Supports full Markdown: headings, lists, links, images, tables,
            fenced code blocks, GFM.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, multiline, testid, list }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </span>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="w-full bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]"
        />
      ) : (
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          list={list}
          className="w-full bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]"
        />
      )}
    </label>
  );
}
